<script setup lang="ts">
/**
 * Rubrique dépliante du bloc « Détails » (Composition, Entretien, Guide des
 * tailles…).
 *
 * ⚠️ POURQUOI PAS `<details>`/`<summary>` NATIFS ICI.
 * La version précédente les utilisait avec l'astuce `grid-template-rows:
 * 0fr → 1fr`. Elle animait mal, et pas par accident : un navigateur ne rend
 * tout simplement PAS le contenu d'un `<details>` fermé. À l'ouverture, la
 * transition démarre donc sur un contenu qui vient d'apparaître et dont la
 * hauteur n'a jamais été mesurée — d'où le saut. À la fermeture, `open` est
 * retiré instantanément et le contenu disparaît avant que la transition
 * inverse n'ait pu jouer. Les contournements (intercepter le clic, retarder
 * le retrait de `open`) ne corrigent que la moitié du problème et rendent le
 * composant fragile.
 *
 * Approche retenue : un `<button>` + un panneau TOUJOURS présent dans le DOM,
 * dont on anime la hauteur réelle mesurée (`scrollHeight`). C'est prévisible
 * dans les deux sens, et ça préserve ce qui compte pour nous :
 *  · le contenu reste dans le HTML SSR même replié → indexable par Google et
 *    trouvable au Ctrl+F du navigateur (c'est la raison pour laquelle on a
 *    retiré les onglets) ;
 *  · `aria-expanded` / `aria-controls` décrivent l'état aux lecteurs d'écran,
 *    ce que `<details>` faisait nativement et qu'il ne faut pas perdre.
 */
const props = defineProps<{
  /** Intitulé de la rubrique (« Composition »…). */
  title: string;
  /** Rubrique ouverte au premier rendu (la 1re l'est, pas les suivantes). */
  defaultOpen?: boolean;
}>();

const isOpen = ref(!!props.defaultOpen);
const panelRef = ref<HTMLElement | null>(null);

/**
 * Hauteur appliquée au panneau. `null` = « auto » : une fois l'ouverture
 * terminée on relâche la hauteur fixe, sinon un contenu qui change de taille
 * (image chargée, texte replié) resterait tronqué.
 */
const height = ref<string | null>(props.defaultOpen ? null : "0px");

const uid = useId();
const panelId = computed(() => `boutique-sec-${uid}`);

function toggle() {
  const el = panelRef.value;
  if (!el) {
    isOpen.value = !isOpen.value;
    return;
  }

  if (isOpen.value) {
    // FERMETURE en DEUX temps. Le panneau est en `height: auto`, valeur non
    // animable : il faut d'abord le figer à sa hauteur réelle, PUIS le
    // ramener à 0.
    //
    // ⚠️ Les deux écritures ne peuvent pas tenir dans le même cycle : Vue
    // regroupe les mises à jour du DOM, seule la dernière valeur serait
    // appliquée et le navigateur ne verrait jamais le point de départ — d'où
    // une fermeture sèche (le défaut constaté). On attend donc que la hauteur
    // figée soit RÉELLEMENT peinte avant de déclencher le repli.
    height.value = `${el.scrollHeight}px`;
    isOpen.value = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        height.value = "0px";
      });
    });
    return;
  }

  // OUVERTURE : le panneau est à `0px` (valeur concrète), on peut viser
  // directement sa hauteur de contenu. `scrollHeight` est mesurable même
  // replié — le panneau est dans le DOM, seule sa hauteur est contrainte.
  isOpen.value = true;
  height.value = `${el.scrollHeight}px`;
}

/**
 * Fin de transition : à l'ouverture on relâche la hauteur fixe pour que le
 * contenu puisse grandir ensuite (image qui charge, texte qui change) sans
 * rester tronqué. À la fermeture on ne touche à rien : `0px` doit tenir.
 */
function onTransitionEnd(event: TransitionEvent) {
  // Ignore les transitions remontées par les enfants (le contenu de la
  // rubrique peut lui-même animer une couleur au survol).
  if (event.target !== panelRef.value || event.propertyName !== "height") return;
  if (isOpen.value) height.value = null;
}
</script>

<template>
  <div class="border-t border-gray-200 dark:border-[#262626]">
    <h3>
      <button
        type="button"
        class="w-full flex items-center justify-between gap-3 text-left min-h-12 py-3 text-[#2d2e5c] dark:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 rounded-xl"
        :aria-expanded="isOpen"
        :aria-controls="panelId"
        @click="toggle"
      >
        <!-- Titre de RUBRIQUE : `text-lg` (≈18px) contre `text-sm` (≈14px) du
             corps. Un titre en gras à la même taille que le paragraphe se lit
             comme de l'emphase dans le texte, pas comme un intitulé. -->
        <span class="text-lg md:text-xl font-bold leading-tight min-w-0">
          {{ title }}
        </span>
        <UIcon
          name="i-lucide-plus"
          class="size-5 shrink-0 text-[#717198] dark:text-gray-400 transition-transform duration-300"
          :class="isOpen ? 'rotate-45' : ''"
          aria-hidden="true"
        />
      </button>
    </h3>

    <!--
      Panneau TOUJOURS rendu (jamais de `v-if`) : replié il reste dans le HTML
      servi, donc indexable et trouvable au Ctrl+F. `aria-hidden` le retire des
      lecteurs d'écran quand il est fermé, et `inert` empêche le clavier
      d'atteindre un lien invisible.
    -->
    <div
      :id="panelId"
      ref="panelRef"
      class="boutique-sec-panel"
      :style="{ height: height ?? 'auto' }"
      :aria-hidden="!isOpen"
      :inert="!isOpen"
      @transitionend="onTransitionEnd"
    >
      <div class="pb-4">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.boutique-sec-panel {
  overflow: hidden;
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (prefers-reduced-motion: reduce) {
  .boutique-sec-panel {
    transition: none;
  }
}
</style>
